import React, { useState } from 'react';
import { useMappedInDirections } from '../../hooks/useMappedInDirections';
import { removeSSMLTags, enhanceDirectionsWithAI, getConcatenatedDirectionsText } from '../../services/mappedInDirectionsService';
import { useMappedInSettings } from '../../hooks/useMappedInSettings';

const MappedInDirectionsButton = ({ 
  spaceName, 
  onDirectionsFetched, 
  buttonText = "🗺️ Get MappedIn Directions",
  buttonStyle = {},
  disabled = false,
  showStyleOptions = false, // Deprecated - kept for backward compatibility
  userId = null,
  locationId = null,
  useAIEnhancement = true,
}) => {
  // Read MappedIn settings (includes mappedInStartLocation and mappedInStartLocationName)
  const { settings } = useMappedInSettings(userId);

  const { isLoading, error, fetchDirections } = useMappedInDirections(userId);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancementError, setEnhancementError] = useState(null);

  const handleFetchDirections = async () => {
    if (!spaceName) {
      alert('Please select a MappedIn space first');
      return;
    }

    setEnhancementError(null);

    // Use mapped-in start location from settings if available
    const externalId = null;
    const startLocation = settings && settings.mappedInStartLocation ? settings.mappedInStartLocation : null;
    const startLocationName = settings && settings.mappedInStartLocationName ? settings.mappedInStartLocationName : null;

    const result = await fetchDirections(externalId, spaceName, startLocation, startLocationName);
    
    if (result && result.success && onDirectionsFetched) {
      // Get raw directions text (no SSML, no extra formatting)
      const rawDirections = removeSSMLTags(result.text);
      let displayText = rawDirections;
      
      // AI Enhancement: If enabled and locationId is provided, enhance ONLY the directions part
      if (useAIEnhancement && userId && locationId) {
        try {
          setIsEnhancing(true);
          console.log("[MappedInDirectionsButton] Enhancing directions with AI...");
          console.log("[MappedInDirectionsButton] Raw directions:", rawDirections);
          
          const enhancementResult = await enhanceDirectionsWithAI(
            userId,
            locationId,
            rawDirections,
            spaceName,
            spaceName
          );
          
          if (enhancementResult.success && enhancementResult.enhancedDirections) {
            console.log("[MappedInDirectionsButton] AI enhancement successful");
            
            // Now get the concatenated text with code inserts from backend
            // This will use the hierarchy: Location → SubCategory → Category → Global
            const concatenatedResult = await getConcatenatedDirectionsText(
              userId,
              locationId,
              spaceName,
              null, // categoryId - backend will determine from location
              null  // subCategoryId - backend will determine from location
            );
            
            if (concatenatedResult.success && concatenatedResult.concatenatedText) {
              const finalText = concatenatedResult.concatenatedText;
              displayText = removeSSMLTags(finalText);
              
              console.log("[MappedInDirectionsButton] Final concatenated text:", finalText);
              console.log("[MappedInDirectionsButton] Display text (no SSML):", displayText);
              
              // Pass the fully concatenated text
              onDirectionsFetched(finalText, { 
                ...result, 
                text: finalText, 
                displayText: displayText,
                aiEnhanced: true,
                config: concatenatedResult.config
              });
              
              setIsEnhancing(false);
              return;
            }
          }
        } catch (enhanceError) {
          console.error("[MappedInDirectionsButton] AI enhancement failed:", enhanceError);
          setEnhancementError(enhanceError.message || "Failed to enhance directions with AI");
          // Continue with non-enhanced directions
        } finally {
          setIsEnhancing(false);
        }
      }
      
      // If no AI enhancement or it failed, use the original raw directions
      console.log("[MappedInDirectionsButton] Using original directions:", rawDirections);
      
      onDirectionsFetched(rawDirections, { 
        ...result, 
        text: rawDirections, 
        displayText: displayText,
        aiEnhanced: false
      });
    }
  };

  const styleOptions = [
    { value: 'narrative', label: '📖 Narrative (Flowing story)', description: 'Natural, story-like directions' },
    { value: 'conversational', label: '💬 Conversational (Friendly)', description: 'Like talking to a friend' },
    { value: 'concise', label: '⚡ Concise (Brief)', description: 'Short and to the point' },
    { value: 'detailed', label: '📋 Detailed (Comprehensive)', description: 'Step-by-step with landmarks' }
  ];

  const isProcessing = isLoading || isEnhancing;

  return (
    <div className="mappedin-directions-button-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleFetchDirections}
          disabled={disabled || isProcessing || !spaceName}
          style={{
            padding: '8px 16px',
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: disabled || isProcessing || !spaceName ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            opacity: disabled || isProcessing || !spaceName ? 0.6 : 1,
            ...buttonStyle
          }}
        >
          {isLoading ? '🔄 Fetching...' : isEnhancing ? '🤖 Enhancing with AI...' : buttonText}
        </button>  
      </div>

      {/* Removed style options panel - no longer needed */}
      
      {error && (
        <div style={{
          marginTop: '8px',
          padding: '8px 12px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          fontSize: '14px',
          border: '1px solid #f5c6cb'
        }}>
          ❌ {error}
        </div>
      )}
      
      {enhancementError && (
        <div style={{
          marginTop: '8px',
          padding: '8px 12px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          fontSize: '14px',
          border: '1px solid #ffeaa7'
        }}>
          ⚠️ AI Enhancement: {enhancementError} (Using original directions)
        </div>
      )}
      
      {!spaceName && (
        <div style={{
          marginTop: '8px',
          padding: '8px 12px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          fontSize: '14px',
          border: '1px solid #ffeaa7'
        }}>
          ⚠️ Please select a MappedIn space first
        </div>
      )}
    </div>
  );
};

export default MappedInDirectionsButton;